import React from 'react';
import { getPhoto, KTCard, KTCardBody } from '../../../../_metronic/helpers';
import { useListModelQuery } from '../../../store/services/apuestesAPI';

const AnalyticsWidgets: React.FC = () => {
  const { data: usersResponse, isLoading, error } = useListModelQuery({
    model: 'users',
    query: 'page=1&limit=20', // Obtener los 20 usuarios más recientes
    includes: 'country,roles,wallet,subscriptions,services,following,photo'
  });

  // Función para generar actividades basadas en datos reales de usuarios
  const generateRecentActivity = () => {
  if (!usersResponse?.data || !Array.isArray(usersResponse.data)) {
    return [];
  }

  const users = usersResponse.data;

    // Crear una copia del array para poder ordenarlo (evitar error de read-only)
    const sortedUsers = [...users].sort((a: any, b: any) => {
      const dateA = a.base?.last_login ? new Date(a.base.last_login) : new Date(0);
      const dateB = b.base?.last_login ? new Date(b.base.last_login) : new Date(0);
      return dateB.getTime() - dateA.getTime();
    });


    // Tomar los 5 usuarios con último login más reciente
    return sortedUsers.slice(0, 6).map((user: any, index: number) => {
      // Obtener alias desde usersResponse.data
      const userAlias = user.alias;
      const userName = userAlias || user.firstname || user.email || 'Usuario';
      
      // Obtener last_login desde usersResponse.data
      const lastLoginValue = user.last_login;
      const lastLogin = lastLoginValue ? new Date(lastLoginValue) : new Date();
      const timeAgo = getTimeAgo(lastLogin);

      const avatar = getPhoto('USER', (user as any)?.photo?.filename, 'small');
      
      
      return {
        id: index + 1,
        avatar: avatar,
        user: userName,
        time: timeAgo,
        icon: 'fas fa-sign-in-alt',
        color: 'primary'
      };
    });
  };

  // Función para calcular tiempo transcurrido
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'hace un momento';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `hace ${hours} hora${hours > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `hace ${days} día${days > 1 ? 's' : ''}`;
    } else {
      const months = Math.floor(diffInSeconds / 2592000);
      return `hace ${months} mes${months > 1 ? 'es' : ''}`;
    }
  };

  const recentActivity = generateRecentActivity();

  // Debug logs para verificar actividades


  if (isLoading) {
    return (
      <div className="d-flex flex-column">
        <div className="mb-6">
          <KTCard className="h-md-100">
            <div className="card-header border-0 pt-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold fs-3 mb-1">Recent Activity</span>
                <span className="text-muted fw-semibold fs-7">Latest user actions</span>
              </h3>
            </div>
            <KTCardBody className="d-flex flex-center p-8">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex flex-column">
        <div className="mb-6">
          <KTCard className="h-md-100">
            <div className="card-header border-0 pt-5">
              <h3 className="card-title align-items-start flex-column">
                <span className="card-label fw-bold fs-3 mb-1">Recent Activity</span>
                <span className="text-muted fw-semibold fs-7">Latest user actions</span>
              </h3>
            </div>
            <KTCardBody className="d-flex flex-center p-8">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle text-warning fs-1 mb-3"></i>
                <div className="text-muted">Error al cargar la actividad reciente</div>
                <small className="text-muted">
                  {JSON.stringify(error, null, 2)}
                </small>
                <div className="mt-2">
                  <button 
                    className="btn btn-sm btn-primary" 
                    onClick={() => window.location.reload()}
                  >
                    Recargar página
                  </button>
                </div>
              </div>
            </KTCardBody>
          </KTCard>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column">
      {/* Recent Activity */}
      <div className="mb-6">
        <KTCard className="h-md-100">
          <div className="card-header border-0 pt-5">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">Recent Activity</span>
              <span className="text-muted fw-semibold fs-7">Latest user actions</span>
            </h3>
          </div>
          <KTCardBody className="pt-0">
            <div className="d-flex flex-column">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="d-flex align-items-center py-3 border-bottom border-gray-300">
                    <div className="symbol symbol-40px me-3">
                      <div className="symbol-label">
                        <img src={activity.avatar} alt={activity.user} className="rounded-circle w-100 h-100" />
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-bold text-gray-800">{activity.user}</div>
                      <div className="fs-7 text-muted">Último acceso</div>
                    </div>
                    <div className="fs-7 text-muted">{activity.time}</div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-info-circle text-muted fs-1 mb-3"></i>
                  <div className="text-muted">No hay actividad reciente</div>
                </div>
              )}
            </div>
          </KTCardBody>
        </KTCard>
      </div>
    </div>
  );
};

export { AnalyticsWidgets };
